// src/components/superadmin/WhatsAppConfiguration.jsx
// Super admin page for configuring WhatsApp branding per organisation
// Follows the PayNowConfiguration pattern: loads all organisations, card-based selection, edit form

import React, { useState, useEffect } from 'react';
import {
  collection,
  getDocs,
  doc,
  getDoc,
  updateDoc,
  serverTimestamp
} from 'firebase/firestore';
import { db } from '../../utils/firebase';

// Org display name lives in branding.name (profile.name is the legacy location)
const getOrgName = (orgData) => orgData.branding?.name || orgData.profile?.name || '';

const WhatsAppConfiguration = () => {
  const [orgs, setOrgs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showConfigForm, setShowConfigForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });

  const [formData, setFormData] = useState({
    orgId: '',
    orgName: '',
    displayName: '',
    businessName: '',
    isActive: false
  });

  useEffect(() => {
    loadOrgs();
  }, []);

  const loadOrgs = async () => {
    try {
      setLoading(true);
      const orgsSnapshot = await getDocs(collection(db, 'organizations'));
      const orgsList = [];

      orgsSnapshot.forEach((docSnap) => {
        const orgData = docSnap.data();
        const branding = orgData.whatsappBranding || {};
        orgsList.push({
          orgId: docSnap.id,
          orgName: getOrgName(orgData) || docSnap.id,
          displayName: branding.displayName || getOrgName(orgData),
          businessName: branding.businessName || getOrgName(orgData),
          isActive: branding.isActive || false
        });
      });

      setOrgs(orgsList);
    } catch (error) {
      console.error('Error loading WhatsApp configurations:', error);
      setMessage({ type: 'error', text: 'Failed to load organisation configurations' });
    } finally {
      setLoading(false);
    }
  };

  const handleEditConfig = async (orgId) => {
    try {
      const orgDoc = await getDoc(doc(db, 'organizations', orgId));

      if (orgDoc.exists()) {
        const orgData = orgDoc.data();
        const branding = orgData.whatsappBranding || {};

        setFormData({
          orgId: orgId,
          orgName: getOrgName(orgData) || orgId,
          displayName: branding.displayName || getOrgName(orgData),
          businessName: branding.businessName || getOrgName(orgData),
          isActive: branding.isActive || false
        });

        setShowConfigForm(true);
        setMessage({ type: '', text: '' });
      }
    } catch (error) {
      console.error('Error loading organisation WhatsApp config:', error);
      setMessage({ type: 'error', text: 'Failed to load organisation configuration' });
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setMessage({ type: '', text: '' });

      if (!formData.displayName.trim()) {
        setMessage({ type: 'error', text: 'Display Name is required' });
        return;
      }

      const orgRef = doc(db, 'organizations', formData.orgId);

      await updateDoc(orgRef, {
        whatsappBranding: {
          displayName: formData.displayName.trim(),
          businessName: formData.businessName.trim() || formData.displayName.trim(),
          isActive: formData.isActive,
          updatedAt: serverTimestamp()
        }
      });

      setMessage({ type: 'success', text: `WhatsApp branding saved for ${formData.orgName}` });

      // Refresh organisation list
      await loadOrgs();

      setTimeout(() => {
        setShowConfigForm(false);
        setMessage({ type: '', text: '' });
      }, 2000);

    } catch (error) {
      console.error('Error saving WhatsApp config:', error);
      setMessage({ type: 'error', text: 'Failed to save configuration: ' + error.message });
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setShowConfigForm(false);
    setMessage({ type: '', text: '' });
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  if (loading) {
    return (
      <div className="whatsapp-config-editor">
        <div className="whatsapp-config-loading">Loading organisation configurations...</div>
      </div>
    );
  }

  // Edit form view
  if (showConfigForm) {
    return (
      <div className="whatsapp-config-editor">
        <div className="whatsapp-config-header">
          <div>
            <h2>WhatsApp Branding — {formData.orgName}</h2>
            <p className="whatsapp-config-subtitle">
              Configure how WhatsApp messages appear for this organisation's members.
              All messages are sent from BespokeBridge's WhatsApp number, with the organisation name shown in the message body.
            </p>
          </div>
        </div>

        {message.text && (
          <div className={`whatsapp-config-message ${message.type}`}>
            {message.text}
          </div>
        )}

        <div className="whatsapp-config-form">
          <div className="form-section">
            <h3>Organisation Information</h3>

            <div className="form-group">
              <label>Org ID</label>
              <input type="text" className="form-input" value={formData.orgId} disabled />
            </div>

            <div className="form-group">
              <label>Organisation Name</label>
              <input type="text" className="form-input" value={formData.orgName} disabled />
            </div>
          </div>

          <div className="form-section">
            <h3>WhatsApp Branding</h3>
            <p className="form-section-description">
              These values are shown in WhatsApp messages sent to this organisation's members.
            </p>

            <div className="form-group">
              <label htmlFor="displayName">Display Name <span className="required">*</span></label>
              <input
                type="text"
                id="displayName"
                name="displayName"
                value={formData.displayName}
                onChange={handleInputChange}
                placeholder="e.g., WiCare"
                className="form-input"
              />
              <span className="form-help">
                Shown as a prefix in WhatsApp messages: <strong>*[{formData.displayName || 'Org Name'}]*</strong>
              </span>
            </div>

            <div className="form-group">
              <label htmlFor="businessName">Business Name</label>
              <input
                type="text"
                id="businessName"
                name="businessName"
                value={formData.businessName}
                onChange={handleInputChange}
                placeholder="e.g., WiCare Ltd"
                className="form-input"
              />
              <span className="form-help">
                Shown in the message footer. Defaults to Display Name if left blank.
              </span>
            </div>

            <div className="form-group checkbox-group">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  name="isActive"
                  checked={formData.isActive}
                  onChange={handleInputChange}
                />
                <span>Enable WhatsApp messaging for this organisation</span>
              </label>
              <span className="form-help">
                When disabled, this organisation's admin cannot send WhatsApp campaigns.
              </span>
            </div>
          </div>

          {/* Message Preview */}
          <div className="form-section">
            <h3>Message Preview</h3>
            <div className="whatsapp-message-preview">
              <div className="preview-header">BespokeBridge</div>
              <div className="preview-body">
                <strong>*[{formData.displayName || 'Org Name'}]*</strong>
                <br /><br />
                Hi Sarah, your support group session tomorrow at 10am is confirmed. See you then!
                <br /><br />
                <em>— {formData.businessName || formData.displayName || 'Org Name'}</em>
              </div>
            </div>
          </div>

          <div className="whatsapp-config-actions">
            <button
              className="config-btn cancel-btn"
              onClick={handleCancel}
              disabled={saving}
            >
              Cancel
            </button>
            <button
              className="config-btn save-btn"
              onClick={handleSave}
              disabled={saving}
            >
              {saving ? 'Saving...' : 'Save Branding'}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Organisation list view (card-based, like PayNow)
  return (
    <div className="whatsapp-config-editor">
      <div className="whatsapp-config-header">
        <div>
          <h2>WhatsApp Configuration</h2>
          <p className="whatsapp-config-subtitle">
            Configure WhatsApp branding for each organisation. All messages are sent from BespokeBridge's central WhatsApp Business Account.
          </p>
        </div>
      </div>

      {message.text && (
        <div className={`whatsapp-config-message ${message.type}`}>
          {message.text}
        </div>
      )}

      {orgs.length === 0 ? (
        <div className="whatsapp-config-loading">
          No organisations found. Create a client first.
        </div>
      ) : (
        <div className="config-cards-grid">
          {orgs.map(org => (
            <div key={org.orgId} className="config-card">
              <div className="config-card-header">
                <h3>{org.orgName}</h3>
                <span className={`status-badge ${org.isActive ? 'active' : 'inactive'}`}>
                  {org.isActive ? 'Active' : 'Inactive'}
                </span>
              </div>
              <div className="config-card-body">
                <div className="config-field">
                  <label>Display Name:</label>
                  <span>{org.displayName || '—'}</span>
                </div>
                <div className="config-field">
                  <label>Business Name:</label>
                  <span>{org.businessName || '—'}</span>
                </div>
                <div className="config-field">
                  <label>Org ID:</label>
                  <span>{org.orgId}</span>
                </div>
              </div>
              <div className="config-card-footer">
                <button
                  className="config-btn edit-btn"
                  onClick={() => handleEditConfig(org.orgId)}
                >
                  Edit Configuration
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default WhatsAppConfiguration;
